import React, { useContext } from 'react';
import cx from 'classnames';
import { FlowContext } from '../../core/flow-engine';
import styles from './RPSOneCallRolloverIRAConfirmationScreen.css';
import Heading from '../../components/Typography/Heading/Heading';
import SectionHeading from '../../components/Typography/SectionHeading/SectionHeading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import { randomKey, getCookie } from '../../core/util';
import {
  checkAccountNumberExist,
  getS3PDFFileName,
  ocrAccountFailureMessage,
  redirectToAlternateOCRFlow
} from './util';
import { isRPSOCRTraditionalAccount } from '../../flows/front-office/shared/utils';
import {
  getDefaultRPSToAccountType,
  getExistingRPSToAccountType,
  getOCRPageTitle,
  getRPSToAccountTypeForDisplay
} from '../../flows/front-office/ocr-flows/utils';

export const ocrPDFBtnText = `Please click this button to generate the Signature paperwork. A PDF will then be generated. If DocuSign was selected, it will be sent to this shared location: \\\\tc241\\OCR\\Archive\\Retail. Otherwise, for USPS it will be sent directly to the vendor for mailing.`;

export const ocrPDFSuccessText =
  'PDF Signature paperwork creation successful. If DocuSign was selected, please check this shared location to access the file. \\\\tc241\\OCR\\Archive\\Retail. Otherwise, for USPS it will be sent directly to the vendor for mailing.';
export const ocrPDFFailureText = `The PDF was not successfully created. Please click the button and try again,
or follow the alternate procedure using the IRA New Account form.`;

const RPSOneCallRolloverPDFSection = ({
  success,
  s3FileName,
  shouldGenerateOCRPDF = true,
  next
}) => {
  const handlePDFCreation = async () => {
    await next('ocrPDF')();
  };

  return (
    <div className="review-section">
      <div className="section-header">
        <SectionHeading text="Create PDF Signature Paperwork" />
        {success ? (
          <>
            <div className="ocr-pdf-success-text">{ocrPDFSuccessText}</div>
            <div className="ocr-pdf-success-text">
              <strong>{getS3PDFFileName(s3FileName)}</strong>
            </div>
          </>
        ) : (
          <>
            <div className="btn-container">
              <MFBeaconButton
                id="ocrPDFCreationBtn"
                data-name="ocrPDFCreationBtn"
                noButtonMargin
                onClick={handlePDFCreation}
                variant="primary"
                label="Generate PDF"
              />
            </div>
            <div
              className={cx(
                { 'pdf-warning-text': !shouldGenerateOCRPDF },
                'section-text'
              )}>
              {!shouldGenerateOCRPDF ? ocrPDFFailureText : ocrPDFBtnText}
            </div>
          </>
        )}
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

/* eslint-disable complexity */

// ***********************************************************************
// TODO [2022-02-01] @Jay: Move of some the logic into a sepearte component
// to reduce lint complexity
// ***********************************************************************
const RPSOneCallRolloverIRASuccessConfirmationScreen = ({
  firstName,
  chosenFundsDetails,
  userHaveExistingAgreements,
  accounts,
  success,
  s3FileName,
  shouldGenerateOCRPDF,
  next,
  accountType,
  bpId,
  inOCRAltFlow = false,
  isOCRTraditionalAcc,
  toAccountTypeText
}) => {
  const altFlowAccountText = isOCRTraditionalAcc ? 'Roth' : 'Traditional';

  const pageTitle = getOCRPageTitle(accountType);

  const subHeadingText = `Your ${toAccountTypeText} IRA account is ready, ${firstName}`;

  const planDistributionText = `Return the Plan Distribution paperwork and include the ${toAccountTypeText} 
  IRA account number in the withdrawal paperwork.`;

  const altFlowSectionHeadingText = `Launch ${altFlowAccountText} Rollover OCR`;

  const getFundName = (chosenInv, acctrpCode) => {
    const matchedFund = chosenInv?.find(ci => ci.trpCode === acctrpCode);
    return matchedFund?.name;
  };

  return (
    <div className="wrapper">
      <Heading text={pageTitle} />
      <SubHeading text={subHeadingText} />

      {userHaveExistingAgreements || (
        <RPSOneCallRolloverPDFSection
          success={success}
          s3FileName={s3FileName}
          shouldGenerateOCRPDF={shouldGenerateOCRPDF}
          next={next}
        />
      )}

      <div className="review-section">
        <div className="section-header">
          <SectionHeading text="Steps You Need to Take to Complete Your Account Setup" />
        </div>
        {!userHaveExistingAgreements ? (
          <>
            <div className="section-text">
              The new {toAccountTypeText} IRA account is established, but there
              are a couple steps needed to complete the rollover process.
            </div>
            <div className="section-info-text">
              1. Sign the Signature page and return it to T. Rowe Price. Your
              signature is needed in order to fully establish your new account
              and prevent restrictions placed on the account after 60 days.
            </div>
            <div className="section-info-text">2. {planDistributionText}</div>
          </>
        ) : (
          <>
            <div className="section-text">
              The request has been submitted for the {toAccountTypeText} IRA,
              but there is another step needed to complete the rollover process.
            </div>
            <div className="section-info-text">1. {planDistributionText}</div>
          </>
        )}
      </div>

      <div className="review-section">
        <div className="section-header">
          <SectionHeading text="Account Information" />
          <div className="section-text">
            A confirmation of your new account information will be sent to you.
            You can also access your account information online to view and make
            any updates as needed. 
          </div>
          <div className="section-text">
            <div className="qv-row">
              <div className="qv-col">
                <div className="field-label">Account Type:</div>
                <div className="field-value">{toAccountTypeText} IRA</div>
              </div>
            </div>
            <div className="qv-row">
              <div className="qv-col">
                <div className="field-label">Fund Name:</div>
                <div className="field-value">
                  {accounts?.map(account => {
                    return (
                      <div key={randomKey()}>
                        <span>
                          {' '}
                          {getFundName(
                            chosenFundsDetails,
                            account.investmentVehicle.trpCode
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="qv-col">
                <div className="field-label">Account Number:</div>
                <div className="field-value">
                  {accounts?.map(account => {
                    return (
                      <div key={randomKey()}>
                        <span> {account.accountNumber}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {inOCRAltFlow || (
        <div className="review-section">
          <div className="section-header">
            <SectionHeading text={altFlowSectionHeadingText} />
            <div className="section-text">
              Click here to launch the {altFlowAccountText} Rollover OCR flow
              for the same client
            </div>
          </div>
          <div>
            <MFBeaconButton
              onClick={() => redirectToAlternateOCRFlow(accountType, bpId)}
              variant="primary"
              label={`Launch ${altFlowAccountText} Rollover`}
            />
          </div>
        </div>
      )}

      <style jsx>{styles}</style>
    </div>
  );
};

/* eslint-enable complexity */

const RPSOneCallRolloverIRAFailureConfirmationScreen = ({
  firstName,
  accountType,
  ocr504Timeout,
  toAccountTypeText
}) => {
  const subHeadingText = `Your ${toAccountTypeText} IRA account was unable to be established, ${firstName}`;

  const subHeadingTextFor504 = `Your ${toAccountTypeText} IRA account is taking longer than expected and has not been established yet, ${firstName}`;

  const pageTitle = getOCRPageTitle(accountType);

  return (
    <div className="wrapper">
      <Heading text={pageTitle} />
      <SubHeading
        text={`${ocr504Timeout ? subHeadingTextFor504 : subHeadingText}`}
      />

      <div className="review-section">
        <div className="section-header">
          <SectionHeading text="Steps You Need to Take to Complete Your Account Setup" />
          <div className="section-text">
            {ocrAccountFailureMessage(toAccountTypeText, ocr504Timeout)}
          </div>
        </div>
      </div>

      {ocr504Timeout && (
        <>
          <div className="review-section">
            <div className="section-text">
              You may also try to relaunch the flow, and if the account setup
              was successful, the account will display in the existing funds
              section.
            </div>
          </div>
          <div className="review-section">
            <div className="section-text">
              If not, please try to re-submit the application.
            </div>
          </div>
        </>
      )}

      <style jsx>{styles}</style>
    </div>
  );
};

const RPSOneCallRolloverIRAConfirmationScreen = ({ appState }) => {
  const {
    store: {
      app: {
        customer: {
          name: { firstName }
        }
      },
      accountType,
      loadedData: {
        chosenFundsDetails,
        accounts,
        success,
        s3FileName,
        ocrSubmissionError,
        serviceErrorCode,
        shouldGenerateOCRPDF,
        userHaveExistingAgreements,
        inOCRAltFlow,
        existingAgreementType,
        hasExistingIRA
      }
    }
  } = appState;

  const { next } = useContext(FlowContext);

  const accountNumbersNotPresent = checkAccountNumberExist(accounts);

  // get the bpId cookie value
  const bpId = getCookie('bpId');

  const ocr504Timeout = serviceErrorCode === 504;

  const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);

  const toAccountTypeText = getRPSToAccountTypeForDisplay(
    hasExistingIRA
      ? getExistingRPSToAccountType(existingAgreementType)
      : getDefaultRPSToAccountType(isOCRTraditionalAcc)
  );

  return (
    <>
      {ocrSubmissionError || accountNumbersNotPresent || ocr504Timeout ? (
        <RPSOneCallRolloverIRAFailureConfirmationScreen
          firstName={firstName}
          accountType={accountType}
          ocr504Timeout={ocr504Timeout}
          toAccountTypeText={toAccountTypeText}
        />
      ) : (
        <RPSOneCallRolloverIRASuccessConfirmationScreen
          firstName={firstName}
          chosenFundsDetails={chosenFundsDetails}
          userHaveExistingAgreements={userHaveExistingAgreements}
          accounts={accounts}
          shouldGenerateOCRPDF={shouldGenerateOCRPDF}
          success={success}
          s3FileName={s3FileName}
          next={next}
          accountType={accountType}
          bpId={bpId}
          inOCRAltFlow={inOCRAltFlow}
          isOCRTraditionalAcc={isOCRTraditionalAcc}
          toAccountTypeText={toAccountTypeText}
        />
      )}
    </>
  );
};

export default RPSOneCallRolloverIRAConfirmationScreen;
